import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { usersApi } from "../api/users"

const initialState = {
  users: [],
  isLoading: false,
  error: null,
}

export const fetchUsers = createAsyncThunk("users/fetchUsers", async (_, { rejectWithValue }) => {
  try {
    const response = await usersApi.getAll()
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка загрузки пользователей")
  }
})

export const createUser = createAsyncThunk("users/createUser", async (userData, { rejectWithValue }) => {
  try {
    const response = await usersApi.create(userData)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка создания пользователя")
  }
})

export const updateUser = createAsyncThunk("users/updateUser", async ({ userId, data }, { rejectWithValue }) => {
  try {
    const response = await usersApi.update(userId, data)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка обновления пользователя")
  }
})

export const deleteUser = createAsyncThunk("users/deleteUser", async (userId, { rejectWithValue }) => {
  try {
    await usersApi.delete(userId)
    return userId
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка удаления пользователя")
  }
})

export const resetPassword = createAsyncThunk(
  "users/resetPassword",
  async ({ userId, newPassword }, { rejectWithValue }) => {
    try {
      await usersApi.resetPassword(userId, newPassword)
      return userId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Ошибка сброса пароля")
    }
  }
)

export const grantSiteAccess = createAsyncThunk(
  "users/grantSiteAccess",
  async ({ userId, siteId, role }, { rejectWithValue }) => {
    try {
      const response = await usersApi.grantSiteAccess(userId, siteId, role)
      return { userId, member: response.data.member }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Ошибка выдачи доступа")
    }
  }
)

export const revokeSiteAccess = createAsyncThunk(
  "users/revokeSiteAccess",
  async ({ userId, siteId }, { rejectWithValue }) => {
    try {
      await usersApi.revokeSiteAccess(userId, siteId)
      return { userId, siteId }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Ошибка отзыва доступа")
    }
  }
)

export const updateSiteRole = createAsyncThunk(
  "users/updateSiteRole",
  async ({ userId, siteId, role }, { rejectWithValue }) => {
    try {
      const response = await usersApi.updateSiteRole(userId, siteId, role)
      return { userId, member: response.data.member }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Ошибка изменения роли")
    }
  }
)

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false
        state.users = action.payload.users
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.unshift(action.payload.user)
      })
      .addCase(createUser.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.user.id)
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...action.payload.user }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((u) => u.id !== action.payload)
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(grantSiteAccess.fulfilled, (state, action) => {
        const user = state.users.find((u) => u.id === action.payload.userId)
        if (user) {
          if (!user.siteMemberships) {
            user.siteMemberships = []
          }
          user.siteMemberships.push(action.payload.member)
        }
      })
      .addCase(grantSiteAccess.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(revokeSiteAccess.fulfilled, (state, action) => {
        const user = state.users.find((u) => u.id === action.payload.userId)
        if (user && user.siteMemberships) {
          user.siteMemberships = user.siteMemberships.filter((m) => m.siteId !== action.payload.siteId)
        }
      })
      .addCase(revokeSiteAccess.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(updateSiteRole.fulfilled, (state, action) => {
        const user = state.users.find((u) => u.id === action.payload.userId)
        if (user && user.siteMemberships) {
          const memberIndex = user.siteMemberships.findIndex((m) => m.siteId === action.payload.member.siteId)
          if (memberIndex !== -1) {
            user.siteMemberships[memberIndex] = action.payload.member
          }
        }
      })
      .addCase(updateSiteRole.rejected, (state, action) => {
        state.error = action.payload
      })
  },
})

export const { clearUsersError } = usersSlice.actions

export const selectUsers = (state) => state.users.users
export const selectUsersLoading = (state) => state.users.isLoading
export const selectUsersError = (state) => state.users.error

export default usersSlice.reducer
